/**
 * Finding a walkthrough's anchors in the live DOM.
 *
 * No React: a walkthrough step is joined to the app by a `data-annie-target` name and nothing else, so
 * deciding which anchors are actually on the page is plain DOM polling. Kept out of the controller so the
 * timing rules below can be read — and tested — without a render.
 */

import { dismissRegisteredSurface } from "./walkthroughDismiss";

/**
 * A step's `anchor` is a NAME, not a selector. This is the one place that turns one into the other.
 *
 * The name arrives off the wire and `annieStream` only proves it is a string, so a quote in it would
 * close the attribute selector and make `querySelector` THROW rather than return null. CI validates every
 * authored anchor, so this is the trust boundary rather than a live bug — and for a well-formed name the
 * replace is the identity.
 *
 * Not `CSS.escape`: that escapes IDENTIFIERS, and this interpolates into a quoted string, where only the
 * quote and the backslash are special. (It is also absent from jsdom, so it would be untestable here.)
 * Anything more exotic than a quote still reaches the caller as a throw, which the launch scan now
 * reports rather than swallows.
 */
const quoted = (value: string): string => value.replace(/["\\]/g, "\\$&");

export const anchorSelector = (anchor: string): string => `[data-annie-target="${quoted(anchor)}"]`;

/**
 * Brings a step's control into view before joyride measures it.
 *
 * joyride finds the scroller through `scrollparent`, which returns the nearest ancestor with ANY overflow —
 * the staff table's horizontal `TableContainer` — and, seeing it can't scroll vertically, falls back to the
 * document. A control deep in the Day view's inner scroll area then stays clipped and joyride lights
 * nothing: a dark page with the tooltip floating over empty space. `scrollIntoView` scrolls every ancestor
 * that needs it. An anchor not mounted yet is a deferred step's; the reveal path scrolls its own trigger.
 */
export const scrollAnchorIntoView = async (anchor: string): Promise<void> => {
	const element = document.querySelector(anchorSelector(anchor));
	if (element === null) return;
	element.scrollIntoView({ block: "center", inline: "nearest" });
	await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
};

/**
 * What an observer waiting for an anchor to APPEAR must watch. An anchor arrives either as a new node or as
 * the attribute landing on a node already mounted — the call-off tour's replacement wrapper holds its name
 * back until the gate answers — and a childList-only watch misses the second until something unrelated
 * happens to mutate the DOM.
 */
export const ANCHOR_ARRIVAL_MUTATIONS: MutationObserverInit = {
	childList: true,
	subtree: true,
	attributes: true,
	attributeFilter: ["data-annie-target"],
};

/**
 * Marks an element whose anchor name lands WITHOUT anyone acting on the opener — the removal dialog's
 * replacement wrapper takes `remove-nurse-replacement` when the gate answers, not when the reason step's
 * control is touched. For such a target only Next counts as the step being answered; a click on the
 * opener (picking a reason) caused nothing. Put it on the element unconditionally, so it is already there
 * the moment the name lands.
 */
export const WALKTHROUGH_ARRIVES_ON_ITS_OWN_ATTR = "data-walkthrough-arrives-on-its-own";

export const TARGET_WAIT_MS = 3000;
const ANCHOR_POLL_MS = 100;
/**
 * How long to keep waiting after the last anchor to APPEAR, not after the first.
 *
 * A flat window from the first arrival cut off anchors that mount later than their siblings — Day
 * Schedule's HPPD figure lives behind a query, so on a slow connection the stats bar was up, the window
 * started, and the step it belonged to was dropped from a tour that had already promised it. Restarting
 * on every new arrival keeps the property this exists for (a genuinely absent control costs ~600ms rather
 * than the full deadline) without punishing a control that is merely slow.
 */
const ANCHOR_GRACE_MS = 600;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Visible, not merely present: joyride skips an unrendered target, so presence alone could satisfy the
 * step floor and then be skipped.
 *
 * Both halves are load-bearing, and the second is not redundant. `getClientRects()` is empty for a
 * detached element and for `display: none`, but a `visibility: hidden` element still generates boxes and
 * reports a rect — measured, not assumed — so rects alone call an invisible control visible. That is
 * precisely the state MUI leaves a `keepMounted` modal in, and `visibility` inherits, so this also
 * catches a control whose hidden ANCESTOR is the modal rather than the control itself.
 */
export const isAnchorVisible = (anchor: string): boolean => {
	const element = document.querySelector(anchorSelector(anchor));
	if (element === null || element.getClientRects().length === 0) return false;
	return window.getComputedStyle(element).visibility !== "hidden";
};

/**
 * How long an anchor must stay gone before the tour treats it as dismissed.
 *
 * Non-zero because an ordinary React re-render detaches and re-attaches a node within a frame, and
 * ending a tour on that would be worse than the strand it is there to prevent. Short because the step is
 * meanwhile describing something the user cannot see.
 */
export const ANCHOR_VANISHED_GRACE_MS = 400;

/**
 * Call `onVanished` once `anchor` has been gone for `ANCHOR_VANISHED_GRACE_MS`; returns the canceller.
 *
 * This is the half joyride does not do. Its `useSpotlightRect` watches for a target to APPEAR — a
 * MutationObserver it disconnects the moment it finds one — and from then on recomputes only on scroll,
 * window resize, and a ResizeObserver bound to that element. None of those fire when the element is
 * removed, so the rect goes stale and the step keeps rendering against a control that is gone.
 *
 * Polled rather than observed, for the same reason `presentAnchors` polls: VISIBILITY is the question,
 * and a control can be dismissed by being hidden rather than unmounted — a MUI modal with `keepMounted`
 * does exactly that — which no MutationObserver on `childList` would ever see. One definition of "there",
 * shared with the launch scan, instead of one rule for deciding a tour's length and a different one for
 * ending it.
 *
 * Arms only for an anchor that is visible NOW: absent already is joyride's acquisition path, which
 * `MISSING_TARGET_WAIT_MS` caps, and claiming it here would end every tour waiting on a deferred reveal.
 */
export const watchAnchor = (anchor: string, onVanished: () => void): (() => void) => {
	if (!isAnchorVisible(anchor)) return () => undefined;
	let missingSince: number | null = null;
	const interval = setInterval(() => {
		if (isAnchorVisible(anchor)) {
			missingSince = null;
			return;
		}
		if (missingSince === null) {
			missingSince = Date.now();
			return;
		}
		if (Date.now() - missingSince < ANCHOR_VANISHED_GRACE_MS) return;
		clearInterval(interval);
		onVanished();
	}, ANCHOR_POLL_MS);
	return () => clearInterval(interval);
};

/** Consecutive frames an anchor's box must be unchanged before the tour treats it as settled. */
const SETTLED_FRAMES = 3;

/**
 * Give up waiting for a box to stop moving. A control that never settles — a spinner, a live figure —
 * must not hold the tour, and a spotlight one frame stale beats a step that never arrives.
 */
export const ANCHOR_SETTLE_CAP_MS = 1000;

/**
 * Run `onSettled` once `anchor`'s box has stopped moving; returns the canceller.
 *
 * joyride measures a step's target ONCE, when the step opens, and then only recomputes on scroll, window
 * resize, and a ResizeObserver bound to that element. A control revealed by a click is usually still
 * animating into place at that moment, and a popover that slides changes its POSITION without changing
 * its SIZE — so the ResizeObserver never fires and the first measurement is the one that sticks.
 *
 * Measured on the staffing-grid tour: advancing the instant the settings panel mounted froze the cutout
 * at y 247-291 while the View type toggle came to rest at y 278-306, lighting the row above it. Waiting
 * for the box to settle first put the same cutout at 268-316, which is the toggle plus its padding.
 *
 * Frames rather than a timer: the question is "has layout stopped changing", which is a per-frame fact,
 * and a fixed delay would be either too short for a slow transition or dead time for an instant one.
 */
export const whenAnchorSettles = (anchor: string, onSettled: () => void): (() => void) => {
	const deadline = Date.now() + ANCHOR_SETTLE_CAP_MS;
	let previous: string | null = null;
	let unchanged = 0;
	let frame = requestAnimationFrame(function tick() {
		const element = document.querySelector(anchorSelector(anchor));
		const box = element === null ? null : JSON.stringify(element.getBoundingClientRect());
		unchanged = box !== null && box === previous ? unchanged + 1 : 0;
		previous = box;
		if (unchanged >= SETTLED_FRAMES || Date.now() >= deadline) {
			onSettled();
			return;
		}
		frame = requestAnimationFrame(tick);
	});
	return () => cancelAnimationFrame(frame);
};

/**
 * Why a scan stopped. Explicit rather than an empty set, because the three cases want opposite handling:
 * `ready` launches, `route-timeout` refuses out loud, and `abandoned` must do NOTHING — the host that
 * would have shown the snackbar is gone.
 */
export type AnchorScan = { outcome: "ready"; anchors: Set<string> } | { outcome: "route-timeout" } | { outcome: "abandoned" };

/** Polled between waits so a scan can give up without sitting out its deadline. */
export type AbandonCheck = () => boolean;

/**
 * Which anchors are on the page once the destination has mounted them.
 *
 * Waits on the ROUTE first, then the anchors. Waiting for "the first anchor to appear" instead looks
 * equivalent and isn't: some anchors are header chrome present on every page, so a tour mixing a header
 * step with a destination step would snapshot against the page the user is leaving.
 *
 * The two waits get SEPARATE budgets. Sharing one meant a slow navigation spent the anchors' time — a
 * 2.9s route left 100ms to find them — and made a tour's completeness depend on how busy the network was.
 *
 * A route that never arrives fails CLOSED. Falling through to scan the current page could match anchors
 * belonging to somewhere else entirely, and header-chrome names are exactly the ones that would match.
 */
export const presentAnchors = async (anchors: string[], expectedPath: string | null, isAbandoned: AbandonCheck): Promise<AnchorScan> => {
	const routeDeadline = Date.now() + TARGET_WAIT_MS;
	while (expectedPath !== null && window.location.pathname !== expectedPath) {
		if (isAbandoned()) return { outcome: "abandoned" };
		if (Date.now() >= routeDeadline) return { outcome: "route-timeout" };
		await sleep(ANCHOR_POLL_MS);
	}

	const anchorDeadline = Date.now() + TARGET_WAIT_MS;
	let graceDeadline: number | null = null;
	let mostSeen = 0;
	while (Date.now() < anchorDeadline) {
		if (isAbandoned()) return { outcome: "abandoned" };
		const visible = anchors.filter(isAnchorVisible);
		if (visible.length === anchors.length) return { outcome: "ready", anchors: new Set(visible) };
		if (visible.length > mostSeen) {
			mostSeen = visible.length;
			graceDeadline = Date.now() + ANCHOR_GRACE_MS;
		}
		if (graceDeadline !== null && Date.now() >= graceDeadline) break;
		await sleep(ANCHOR_POLL_MS);
	}
	if (isAbandoned()) return { outcome: "abandoned" };
	return { outcome: "ready", anchors: new Set(anchors.filter(isAnchorVisible)) };
};

/** How many nested layers the dismissal will unwind. A panel inside a menu is the deepest real case. */
const MAX_DISMISS_DEPTH = 3;

/**
 * The overlay containers a revealed control can sit in. One list, used both to FIND the surface to close
 * and to decide whether two anchors share one — the two answers have to agree or a tour closes a panel it
 * is still standing in.
 */
export const OVERLAY_SURFACE_SELECTOR = ".MuiModal-root, .MuiPopover-root, .MuiDrawer-root";

/**
 * The overlay surface an anchor sits in: `null` when it is on the page itself, `undefined` when the
 * anchor is not in the DOM at all.
 *
 * Identity of the SURFACE is what decides whether the tour has left what it opened — comparing anchor
 * names cannot: the Recurring Shift panel's nurse field, week lists and save button are three anchors
 * inside one panel, so "the current anchor differs from the revealed one" is true while the tour is
 * still standing in it, and closing there would end the tour three steps early.
 *
 * Absent and page-level are kept APART, and that distinction is the whole safety of this. Folding both
 * into `null` made a transiently-missing current anchor — mid-transition, mid-mount — read as "the tour
 * is on the page now", which dismissed the very panel the current step was standing in and then ended the
 * tour when `watchAnchor` saw its control vanish: exactly the failure the surface comparison exists to
 * prevent, reintroduced through the absent case.
 */
export const overlaySurfaceOf = (anchor: string): Element | null | undefined => {
	const element = document.querySelector(anchorSelector(anchor));
	return element === null ? undefined : element.closest(OVERLAY_SURFACE_SELECTOR);
};

/**
 * Whether going Back from `current` lands on a control that is still on screen: the previous step's anchor
 * sits in the SAME open overlay as this one — the removal dialog's three deferred steps, the Recurring Shift
 * panel's fields. After a surface closes (or across nested ones, the composer over the Staffing drawer)
 * Back would target a control that is gone or covered, so it stays hidden there.
 */
export const sharesOpenSurface = (previous: string, current: string): boolean => {
	const surface = overlaySurfaceOf(previous);
	return surface !== undefined && surface !== null && surface === overlaySurfaceOf(current);
};

/**
 * True only while the unwind below is dispatching its own Escape.
 *
 * The tour closes a surface by sending Escape, and the controller listens for Escape to end the tour — so
 * without this it would exit every time it tidied up after itself. A flag rather than `event.isTrusted`:
 * that also rejects the synthetic events a test dispatches to stand in for the user, which would make the
 * "a user pressing Escape ends the tour" rule untestable.
 */
let dispatchingDismissKey = false;
export const isDispatchingDismissKey = (): boolean => dispatchingDismissKey;

/**
 * Close whatever the tour opened, so finishing leaves the screen as the tour found it.
 *
 * Only DEFERRED anchors are considered, and that is what makes this safe: the launch scan proves an
 * eager anchor was already on the page, while a deferred one is known-absent at scan time, so a deferred
 * control that is visible NOW was revealed during the tour. Something the user opened themselves
 * beforehand is never a deferred anchor, so it is never touched.
 *
 * A surface that REGISTERED its own close is asked FIRST, and the order is the point: the Recurring Shift
 * panel's ✕ raises "Discard Changes?" even before anything is entered (opening from an empty `+` seeds a
 * shift, so it already counts as dirty), and answering that dialog works but flashes it on the way out. A
 * surface that says how it wants to be closed gets closed that way, silently.
 *
 * Otherwise Escape, which is the dismissal a user would make themselves and closes MUI's Menu and
 * Popover. Dispatched from the focused element because MUI listens on the surface's own root and traps
 * focus inside it, so a document-level event never arrives.
 *
 * Then the surface's own ✕ as the last resort, for a modal that declares nothing and ignores Escape.
 * `CloseIcon` is MUI's own marker on it, so even this cannot reach Save, Cancel or a confirm — the tour
 * never presses a control that acts on data.
 *
 * Awaited between layers: React has to re-render before the next one is on top.
 *
 * `isAbandoned` is polled before every layer and after every wait, the same way `presentAnchors` polls it.
 * One unwind can be in flight for over three seconds, and the tour it belongs to can end, be replaced, or
 * unmount inside that window — after which a late Escape or ✕ lands on whatever the user has opened since.
 * Ownership is the caller's to express; the default never abandons, which is right for the tour-end call
 * where there is nothing left to invalidate it.
 */
export const dismissRevealedAnchors = async (deferredAnchors: string[], isAbandoned: AbandonCheck = () => false): Promise<void> => {
	for (let depth = 0; depth < MAX_DISMISS_DEPTH; depth++) {
		if (isAbandoned()) return;
		const openAnchor = deferredAnchors.find(isAnchorVisible);
		if (openAnchor === undefined) return;
		const revealed = document.querySelector(anchorSelector(openAnchor));
		if (revealed === null) return;
		// A surface that registered its own close gets to use it, and the order is the point: the Recurring
		// Shift panel's ✕ raises "Discard Changes?" even before anything is entered, and answering that
		// dialog works but flashes it on the way out.
		//
		// RAN is not CLOSED, though. A handler that returns without dismissing would otherwise be retried
		// every pass until the depth cap, and the Escape/✕ fallbacks below — which might have worked —
		// would never be reached. Verified, then fallen through on failure.
		if (dismissRegisteredSurface()) {
			await sleep(ANCHOR_VANISHED_GRACE_MS);
			if (isAbandoned() || !deferredAnchors.some(isAnchorVisible)) return;
			// It DID close its surface when its own anchor is gone, which leaves `revealed` — and the
			// `surface` derived from it below — detached: the fallbacks would then fire Escape at the page
			// and hunt a ✕ inside a node that is no longer in it. Re-resolve against whatever is still open.
			// Falling through stays correct for the RAN-but-closed-nothing case the paragraph above covers.
			if (!isAnchorVisible(openAnchor)) continue;
		}
		const surface = revealed.closest(OVERLAY_SURFACE_SELECTOR) ?? revealed;
		const from = document.activeElement ?? document.body;
		dispatchingDismissKey = true;
		try {
			from.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true }));
		} finally {
			dispatchingDismissKey = false;
		}
		await sleep(ANCHOR_VANISHED_GRACE_MS);
		if (isAbandoned() || !deferredAnchors.some(isAnchorVisible)) return;
		surface.querySelector<HTMLElement>('button:has(svg[data-testid="CloseIcon"])')?.click();
		await sleep(ANCHOR_VANISHED_GRACE_MS);
	}
};

/**
 * The control inside `targetSelector` that a step's Next should press to reveal the next step, scrolled
 * into view — or null when the step's own anchor is the thing to click.
 *
 * Exists because an anchor is sometimes a CONTAINER. The Schedule Template grid has to be the anchor:
 * joyride's overlay only passes clicks through to the element it spotlights, so pointing at one `+` cell
 * would make every other cell unclickable, and the `+` is repeated in ~360 cells, so it cannot be a unique
 * anchor either (joyride failed to resolve a rect for it at all and fell back to a full-viewport
 * spotlight). Marking the trigger separately keeps the spotlight on the container and still lets Next act.
 *
 * The first VISIBLE one, then centred: "first in the DOM" is routinely behind a sticky header — the grid's
 * first `+` sits at y 271 under the sticky day-of-week row — and pressing Next on a control the user
 * cannot see reads as the tour doing something at random.
 */
const usableTrigger = (container: Element): HTMLElement | undefined =>
	// Enabled, not merely visible: the Schedule Template's `+` is disabled where editing is blocked for the
	// department, and clicking a disabled button is a silent no-op — the user would press Next, see nothing
	// happen, and be nudged toward a control that can never respond.
	[...container.querySelectorAll<HTMLElement>("[data-walkthrough-reveal]")].find(
		(candidate) =>
			candidate.getClientRects().length > 0 &&
			window.getComputedStyle(candidate).visibility !== "hidden" &&
			!candidate.hasAttribute("disabled") &&
			candidate.getAttribute("aria-disabled") !== "true",
	);

/**
 * Whether an opener step could actually open the thing the step after it points at.
 *
 * Only ever false for an anchor that MARKS triggers and has no usable one — a department with
 * `disable_edit_master_schedule` renders every `+` disabled, so the grid step is there and looks normal
 * while nothing behind it can be reached. An anchor with no markers at all is the ordinary case, where the
 * anchor IS the control, and is always considered able to reveal.
 */
export const openerCanReveal = (anchor: string): boolean => {
	const container = document.querySelector(anchorSelector(anchor));
	if (container === null) return false;
	if (container.querySelector("[data-walkthrough-reveal]") === null) return true;
	return usableTrigger(container) !== undefined;
};

export const revealTriggerWithin = (targetSelector: string): HTMLElement | null => {
	const container = document.querySelector(targetSelector);
	if (container === null) return null;
	const trigger = usableTrigger(container);
	if (trigger === undefined) return null;
	trigger.scrollIntoView({ block: "center", inline: "nearest" });
	return trigger;
};
